import { BackupService, BackupTenantMismatchError } from '@infra/backup/BackupService';
import { BackupRetentionPolicy } from '@infra/backup/BackupRetentionPolicy';
import type { AppContext } from '@application/context/AppContext';
import { downloadBlob } from '@presentation/shell/download';
import { ToastService } from '@presentation/components/Toast';
import '@presentation/components/Modal';
import { enhancePasswordFields } from '@presentation/components/PasswordVisibility';

export function requestBackupPassword(options: {
  title: string;
  message: string;
  confirmation?: boolean;
}): Promise<string | null> {
  return new Promise((resolve) => {
    const modal = document.createElement('nexxus-modal') as HTMLElement & {
      open: () => void;
      close: () => void;
    };
    modal.setAttribute('title', options.title);
    modal.setAttribute('size', 'sm');
    modal.innerHTML = `
      <form id="backup-password-form" class="modal-body" novalidate>
        <p style="margin:0 0 18px;color:var(--gris);">${options.message}</p>
        <div class="field">
          <label for="backup-dialog-password">Mot de passe</label>
          <input id="backup-dialog-password" type="password" minlength="8" required autocomplete="off" spellcheck="false">
          <span class="hint">8 caractères minimum. Conservez ce mot de passe dans un endroit sûr.</span>
        </div>
        ${
          options.confirmation
            ? `<div class="field" style="margin-top:14px;">
                <label for="backup-dialog-confirmation">Confirmer le mot de passe</label>
                <input id="backup-dialog-confirmation" type="password" minlength="8" required autocomplete="off" spellcheck="false">
              </div>`
            : ''
        }
        <p id="backup-password-error" role="alert" style="display:none;margin:14px 0 0;color:var(--rouge,#b42318);"></p>
        <div style="display:flex;justify-content:flex-end;gap:10px;margin-top:22px;">
          <button type="button" class="btn btn-ghost" data-cancel>Annuler</button>
          <button type="submit" class="btn btn-primary">Continuer</button>
        </div>
      </form>`;
    document.body.appendChild(modal);

    const password = modal.querySelector<HTMLInputElement>('#backup-dialog-password');
    const confirmation = modal.querySelector<HTMLInputElement>('#backup-dialog-confirmation');
    const error = modal.querySelector<HTMLElement>('#backup-password-error');
    let settled = false;
    const finish = (value: string | null): void => {
      if (settled) return;
      settled = true;
      modal.close();
      modal.remove();
      resolve(value);
    };
    const showError = (message: string): void => {
      if (!error) return;
      error.textContent = message;
      error.style.display = 'block';
    };

    modal.addEventListener('close', () => finish(null), { once: true });
    modal.querySelector('[data-cancel]')?.addEventListener('click', () => finish(null));
    modal
      .querySelector<HTMLFormElement>('#backup-password-form')
      ?.addEventListener('submit', (event) => {
        event.preventDefault();
        const value = password?.value ?? '';
        if (value.length < 8) {
          showError('Le mot de passe doit contenir au moins 8 caractères.');
          password?.focus();
          return;
        }
        if (options.confirmation && confirmation?.value !== value) {
          showError('Les mots de passe ne correspondent pas.');
          confirmation?.focus();
          return;
        }
        finish(value);
      });

    modal.open();
    enhancePasswordFields(modal);
    requestAnimationFrame(() => {
      password?.focus();
    });
  });
}

/** Export chiffré par défaut (AES-GCM via BackupService). */
export async function runBackupExport(
  ctx: AppContext,
  password?: string,
  prompt?: { title: string; message: string },
): Promise<boolean> {
  let pwd = password;
  if (!pwd) {
    pwd =
      (await requestBackupPassword({
        title: prompt?.title ?? 'Protéger la sauvegarde',
        message:
          prompt?.message ??
          'Choisissez le mot de passe qui chiffrera le fichier de sauvegarde. Le .bkp contient l’index du coffre-fort, pas les fichiers eux-mêmes : copiez aussi le dossier coffre-fort de l’application pour récupérer les pièces.',
        confirmation: true,
      })) ?? undefined;
    if (!pwd) return false;
  }
  if (pwd.length < 8) {
    ToastService.warning('Mot de passe de sauvegarde : minimum 8 caractères');
    return false;
  }

  const backup = new BackupService(ctx.db);
  try {
    const tenantId = ctx.tenantContext.tenantId();
    const bundle = await backup.export(tenantId);
    downloadBlob(
      await backup.toBlob(bundle, pwd),
      `nexxus-backup-${tenantId}-${new Date().toISOString().slice(0, 10)}.bkp`,
    );
    BackupRetentionPolicy.recordExport();
    const counts = Object.values(bundle.manifest.counts).reduce((s, n) => s + n, 0);
    ToastService.success(
      `Sauvegarde chiffrée : ${counts} entités. ${bundle.manifest.coffrefort_note ?? ''}`.trim(),
    );
    return true;
  } catch (err) {
    ToastService.error(String((err as Error).message));
    return false;
  }
}

export async function runBackupImport(ctx: AppContext): Promise<boolean> {
  if (
    !confirm(
      'La restauration fusionne la sauvegarde avec les données actuelles (les enregistrements absents du fichier sont conservés). Les pièces du coffre-fort restent dans le dossier local : seul l’index est restauré. Un fichier provenant d’une autre entreprise est refusé. Continuer ?',
    )
  )
    return false;

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json,.bkp';
  input.style.display = 'none';
  document.body.appendChild(input);

  const file = await new Promise<File | null>((resolve) => {
    input.onchange = () => resolve(input.files?.[0] ?? null);
    input.click();
  });
  document.body.removeChild(input);
  if (!file) return false;

  const needsPwd = file.name.endsWith('.bkp');
  let pwd: string | undefined;
  if (needsPwd) {
    pwd =
      (await requestBackupPassword({
        title: 'Ouvrir la sauvegarde',
        message: 'Saisissez le mot de passe utilisé lors de la création de ce fichier.',
      })) ?? undefined;
    if (!pwd) return false;
  }

  const backup = new BackupService(ctx.db);
  try {
    const bundle = await backup.fromBlob(file, pwd);
    const result = await backup.import(bundle, { targetTenantId: ctx.tenantContext.tenantId() });
    if (!result.integrity) {
      ToastService.error('Échec vérification d’intégrité (hash SHA-256 différent)');
      return false;
    }
    const total = Object.values(result.restored).reduce((s, n) => s + n, 0);
    ToastService.success(`Restauration réussie : ${total} entités`);
    return true;
  } catch (err) {
    ToastService.error(
      err instanceof BackupTenantMismatchError
        ? err.message
        : `Erreur restauration : ${String((err as Error).message)}`,
    );
    return false;
  }
}
